import React from 'react';
import { Box, Chip, Paper, Typography } from '@mui/material';
import { tokens, getStatusTone, toneColors } from '../../theme';

/**
 * Shared presentational primitives.
 *
 * Each of these patterns previously had two to four divergent
 * implementations across the three dashboards — stat cards with different
 * label/value order, four empty-state styles, section titles at three
 * heading levels, and status colours that disagreed between tabs. These are
 * the one version of each.
 */

// ---------------------------------------------------------------------------
// Section — a titled panel. Replaces the ad-hoc Card/Paper + heading combos.
// ---------------------------------------------------------------------------
export const Section = ({ title, subtitle, actions, children, disablePadding = false, sx }) => (
  <Paper variant="outlined" sx={{ overflow: 'hidden', ...sx }}>
    {(title || actions) && (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          px: 2,
          py: 1.5,
          borderBottom: `1px solid ${tokens.color.border}`,
          flexWrap: 'wrap',
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          {title && (
            <Typography variant="h6" noWrap>
              {title}
            </Typography>
          )}
          {subtitle && (
            <Typography variant="body2" sx={{ color: tokens.color.inkSecondary, mt: 0.25 }}>
              {subtitle}
            </Typography>
          )}
        </Box>
        {actions && <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>{actions}</Box>}
      </Box>
    )}
    <Box sx={disablePadding ? undefined : { p: 2 }}>{children}</Box>
  </Paper>
);

// ---------------------------------------------------------------------------
// SectionHeader — for headings that sit directly on the canvas, not in a panel.
// ---------------------------------------------------------------------------
export const SectionHeader = ({ title, subtitle, actions, sx }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 2,
      flexWrap: 'wrap',
      ...sx,
    }}
  >
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="h5" noWrap>
        {title}
      </Typography>
      {subtitle && (
        <Typography variant="body2" sx={{ color: tokens.color.inkSecondary, mt: 0.25 }}>
          {subtitle}
        </Typography>
      )}
    </Box>
    {actions && <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>{actions}</Box>}
  </Box>
);

// ---------------------------------------------------------------------------
// StatCard — one KPI treatment: label above, figure below, quiet icon.
// ---------------------------------------------------------------------------
export const StatCard = ({ label, value, caption, icon, tone = 'neutral', onClick }) => {
  const t = toneColors[tone] || toneColors.neutral;
  const interactive = typeof onClick === 'function';

  return (
    <Paper
      variant="outlined"
      onClick={onClick}
      sx={{
        p: 2,
        height: '100%',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: 1.5,
        cursor: interactive ? 'pointer' : 'default',
        transition: 'background-color 120ms, border-color 120ms',
        ...(interactive && {
          '&:hover': { bgcolor: tokens.color.surfaceHover, borderColor: tokens.color.borderStrong },
        }),
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="overline" sx={{ color: tokens.color.inkTertiary, display: 'block' }} noWrap>
          {label}
        </Typography>
        <Typography
          sx={{
            fontSize: '1.5rem',
            fontWeight: 650,
            lineHeight: 1.15,
            mt: 0.5,
            fontVariantNumeric: 'tabular-nums',
            letterSpacing: '-0.02em',
          }}
        >
          {value}
        </Typography>
        {caption && (
          <Typography variant="body2" sx={{ color: tokens.color.inkSecondary, mt: 0.5 }} noWrap>
            {caption}
          </Typography>
        )}
      </Box>
      {icon && (
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: 1,
            display: 'grid',
            placeItems: 'center',
            bgcolor: t.bg,
            color: t.fg,
            flexShrink: 0,
            '& .MuiSvgIcon-root': { fontSize: 18 },
          }}
        >
          {icon}
        </Box>
      )}
    </Paper>
  );
};

// ---------------------------------------------------------------------------
// EmptyState — replaces four different empty-state languages.
// ---------------------------------------------------------------------------
export const EmptyState = ({ icon, title, description, action, dense = false }) => (
  <Box
    sx={{
      textAlign: 'center',
      px: 3,
      py: dense ? 4 : 6,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 1,
    }}
  >
    {icon && (
      <Box
        sx={{
          width: 40,
          height: 40,
          mb: 0.5,
          borderRadius: '50%',
          display: 'grid',
          placeItems: 'center',
          bgcolor: tokens.color.surfaceHover,
          color: tokens.color.inkTertiary,
          '& .MuiSvgIcon-root': { fontSize: 20 },
        }}
      >
        {icon}
      </Box>
    )}
    <Typography variant="subtitle1">{title}</Typography>
    {description && (
      <Typography variant="body2" sx={{ color: tokens.color.inkSecondary, maxWidth: 380 }}>
        {description}
      </Typography>
    )}
    {action && <Box sx={{ mt: 1 }}>{action}</Box>}
  </Box>
);

// ---------------------------------------------------------------------------
// StatusChip — a single status vocabulary, so the same status never renders
// in two different colours depending on which tab you are looking at.
// ---------------------------------------------------------------------------
export const StatusChip = ({ status, label, size = 'small', sx }) => {
  const t = toneColors[getStatusTone(status)];
  const text = label || String(status || '—').replace(/_/g, ' ');

  return (
    <Chip
      size={size}
      label={text}
      sx={{
        bgcolor: t.bg,
        color: t.fg,
        textTransform: 'capitalize',
        border: `1px solid ${t.fg}22`,
        ...sx,
      }}
    />
  );
};

// ---------------------------------------------------------------------------
// TableShell — wide tables scroll inside their own container so the page
// body never scrolls sideways.
// ---------------------------------------------------------------------------
export const TableShell = ({ children, sx }) => (
  <Paper variant="outlined" sx={{ overflow: 'hidden', ...sx }}>
    <Box sx={{ overflowX: 'auto' }}>{children}</Box>
  </Paper>
);

export default { Section, SectionHeader, StatCard, EmptyState, StatusChip, TableShell };
